import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { Commit } from '../features/scm/material-management/v1/models/supply-visibility.model';

type MonthKey = 'cs' | 'csFull' | 'en' | 'enFull';

type CommitDateField = {
  field: keyof Commit;
  format: null | string;
};

@Injectable({
  providedIn: 'root',
})
export class DateService {
  constructor(private readonly datePipe: DatePipe) {}

  /**
   * Get the month names
   */
  get monthNames(): {
    cs: string[];
    csFull: string[];
    en: string[];
    enFull: string[];
  } {
    return {
      cs: [
        'Led.',
        'Úno.',
        'Bře.',
        'Dub.',
        'Kvě.',
        'Čvn.',
        'Čvc.',
        'Srp.',
        'Zář.',
        'Říj.',
        'Lis.',
        'Pro.',
      ],
      csFull: [
        'Leden',
        'Únor',
        'Březen',
        'Duben',
        'Květen',
        'Červen',
        'Červenec',
        'Srpen',
        'Září',
        'Říjen',
        'Listopad',
        'Prosinec',
      ],
      en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      enFull: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ],
    };
  }

  /**
   * Get the invalid dates
   * These dates are not valid and should not be displayed, add more if needed
   */
  get invalidDates(): string[] {
    return ['2000-01-01', '0001-01-01', '1899-12-31', '1900-01-01', '0000-00-00'];
  }

  /**
   * Get the date in the correct language
   * @param date
   * @param language
   */
  getDate(date: string, language = 'en'): null | string {
    return this.customFormat(date, ['cs', 'cz'].includes(language) ? 'dd.MM.yyyy' : 'yyyy-MM-dd');
  }

  /**
   * Get the date with year in the correct language
   * @param date
   * @param language
   */
  getDateWithYear(date: string, language = 'en'): string {
    const months = this.getMonths(language);
    const newDate = new Date(date);
    return months[newDate.getMonth()] + ' ' + newDate.getDate() + ' ' + newDate.getFullYear();
  }

  /**
   * Get the date and time in the correct language
   * @param date
   * @param language
   */
  getDateTime(date: string, language = 'en'): null | string {
    return this.customFormat(
      date,
      ['cs', 'cz'].includes(language) ? 'dd.MM.yyyy HH:mm' : 'yyyy-MM-dd HH:mm',
    );
  }

  /**
   * Get the months in the correct language
   * @param language
   * @param short - if true, return short month names
   */
  getMonths(language: string, short = true): string[] {
    const lengthVersion = short ? '' : 'Full';

    const langKey = ['cs', 'cz'].includes(language.toLowerCase()) ? 'cs' : 'en';

    const key = (langKey + lengthVersion) as MonthKey;

    return this.monthNames[key];
  }

  /**
   * Format date with custom format and check if the date is invalid (e.g. 0001-01-01)
   * @param value
   * @param format
   */
  customFormat(value: null | string, format: string = 'yyyy-MM-dd'): null | string {
    if (!value) {
      return '';
    }
    const datePart = value.substring(0, 10);
    if (this.invalidDates.includes(datePart)) {
      return '';
    }
    return this.datePipe.transform(value, format);
  }

  /**
   * Get the date in the correct language based on user's locale
   * @param date
   */
  /**
   * Get the date and time in the correct language based on user's locale
   * @param date
   */
  /**
   * Get the time in the correct language based on user's locale
   * @param date
   */
  /**
   * Compare two dates
   * @param date1
   * @param date2
   * @returns boolean if date1 is greater than date2
   */
  /**
   * Compare two dates and check if they are equal
   * @param date1
   * @param date2
   */
  compareDatesEqual(date1: string, date2: string): boolean {
    return new Date(date1).toDateString() === new Date(date2).toDateString();
  }

  /**
   * Get dates for commit history table
   * @param commit
   */
  commitHistoryTableDates(commit: Commit): Commit {
    const datesList: CommitDateField[] = [
      { field: 'actualETADate', format: null },
      { field: 'deliveryDate', format: null },
      { field: 'eddDate', format: null },
      { field: 'etaDate', format: null },
      { field: 'etaPortDate', format: null },
      { field: 'etdDate', format: null },
      { field: 'expirationDate', format: null },
      { field: 'inboundDeliveryDate', format: null },
      { field: 'receiveDate', format: 'yyyy-MM-dd HH:mm:ss' },
      { field: 'recomitRequestDate', format: null },
      { field: 'scheduleLineDate', format: null },
      { field: 'slotDate', format: 'yyyy-MM-dd HH:mm:ss' },
      { field: 'requestDate', format: null },
      { field: 'triggerDate', format: null },
      { field: 'instructionEtaDate', format: null },
      { field: 'purchasingDocumentDate', format: null },
      { field: 'otmReceivedDate', format: null },
      { field: 'sapDeliveryDate', format: null },
      { field: 'lastUpdateDate', format: 'yyyy-MM-dd HH:mm:ss' },
      { field: 'codeDate', format: null },
      { field: 'arrivalDate', format: 'yyyy-MM-dd HH:mm:ss' },
      { field: 'createDate', format: 'yyyy-MM-dd' },
    ];
    for (const element of datesList) {
      const value = commit[element.field];

      if (typeof value === 'string' && value.trim() !== '') {
        const formattedDate = element.format
          ? this.customFormat(value, element.format)
          : this.getDate(value);

        if (formattedDate !== null) {
          commit[element.field as keyof Commit] = formattedDate;
        }
      }
    }
    return commit;
  }

  /**
   * Get the forecast date in the format YYYY-MM-DD HH:mm
   * @param date
   */
  getForecastDate(date: string): [null | string, null | string[]] {
    const dateTime = this.getDateTime(date);

    if (!dateTime) {
      return [null, null];
    }

    const [datePart, timePart] = dateTime.split(' ');
    return [datePart, [timePart]];
  }
}
